import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import {
  FileText,
  Building2,
  Calendar,
  MoreHorizontal,
  TrendingUp,
  Users,
  Award,
  Target,
  Trash2,
} from 'lucide-react';
import { theme } from '../styles/theme';

const RowContainer = styled.div`
  background: ${theme.colors.surface};
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.borderRadius.xl};
  padding: ${theme.spacing[5]};
  cursor: pointer;
  transition: all ${theme.transitions.base};
  position: relative;
  overflow: hidden;
  
  &:hover {
    border-color: ${theme.colors.primary[300]};
    box-shadow: ${theme.shadows.lg};
    transform: translateY(-1px);
  }
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 3px;
    background: linear-gradient(90deg, ${theme.colors.primary[500]}, ${theme.colors.accent[600]});
    opacity: 0;
    transition: opacity ${theme.transitions.base};
  }
  
  &:hover::before {
    opacity: 1;
  }
`;

const RowHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: ${theme.spacing[3]};
  gap: ${theme.spacing[4]};
`;

const TitleSection = styled.div`
  display: flex;
  align-items: flex-start;
  gap: ${theme.spacing[3]};
  flex: 1;
  min-width: 0;
`;

const DocumentIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: ${theme.borderRadius.lg};
  background: ${theme.colors.primary[100]};
  color: ${theme.colors.primary[600]};
  flex-shrink: 0;
`;

const TitleContent = styled.div`
  flex: 1;
  min-width: 0;
`;

const DocumentTitle = styled.h3`
  font-size: ${theme.typography.fontSize.lg};
  font-weight: ${theme.typography.fontWeight.bold};
  color: ${theme.colors.textPrimary};
  margin: 0 0 ${theme.spacing[1]} 0;
  line-height: ${theme.typography.lineHeight.tight};

  /* Allow title to wrap to 2 lines max */
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  /* word-wrap: break-word only breaks at word boundaries; long URLs
     have none, which lets the title force the parent card wider than
     the viewport. overflow-wrap: anywhere + word-break: break-word
     forces a break at any character when no break opportunity exists. */
  overflow-wrap: anywhere;
  word-break: break-word;
`;

const ScoreSection = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing[2]};
  flex-shrink: 0;
`;

const ScoreBadge = styled.div<{ $score: number | null }>`
  display: flex;
  align-items: center;
  gap: ${theme.spacing[1]};
  padding: ${theme.spacing[1]} ${theme.spacing[3]};
  border-radius: ${theme.borderRadius.full};
  font-size: ${theme.typography.fontSize.sm};
  font-weight: ${theme.typography.fontWeight.bold};

  ${({ $score }) => {
    // null/undefined score → neutral pill (not red, not green) so the
    // user doesn't read it as "scored low". Red is reserved for an
    // actually-bad evaluation score.
    if ($score == null) {
      return `
        background: ${theme.colors.backgroundSecondary};
        color: ${theme.colors.textMuted};
      `;
    }
    if ($score >= 90) {
      return `
        background: ${theme.colors.success[100]};
        color: ${theme.colors.success[700]};
      `;
    } else if ($score >= 70) {
      return `
        background: ${theme.colors.info[100]};
        color: ${theme.colors.info[700]};
      `;
    } else if ($score >= 50) {
      return `
        background: ${theme.colors.warning[100]};
        color: ${theme.colors.warning[700]};
      `;
    } else {
      return `
        background: ${theme.colors.error[100]};
        color: ${theme.colors.error[700]};
      `;
    }
  }}
`;

const IndustryIcon = styled.div<{ $industry: string }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: ${theme.borderRadius.md};
  color: white;
  font-size: 14px;
  
  ${({ $industry }) => {
    switch ($industry.toLowerCase()) {
      case 'infrastructure':
        return `background: linear-gradient(135deg, #6366f1, #8b5cf6);`;
      case 'technology':
        return `background: linear-gradient(135deg, #06b6d4, #3b82f6);`;
      case 'healthcare':
        return `background: linear-gradient(135deg, #10b981, #059669);`;
      case 'energy':
        return `background: linear-gradient(135deg, #f59e0b, #d97706);`;
      case 'education':
        return `background: linear-gradient(135deg, #ec4899, #be185d);`;
      default:
        return `background: linear-gradient(135deg, ${theme.colors.primary[500]}, ${theme.colors.accent[600]});`;
    }
  }}
`;

const ActionButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  background: transparent;
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.borderRadius.md};
  color: ${theme.colors.textMuted};
  cursor: pointer;
  transition: all ${theme.transitions.base};

  &:hover {
    background: ${theme.colors.primary[50]};
    border-color: ${theme.colors.primary[300]};
    color: ${theme.colors.primary[600]};
  }
`;

// Anchor wrapper for the action button + popover. Positioned relatively
// so the popover can absolutely-position underneath without escaping the
// row's stacking context.
const ActionAnchor = styled.div`
  position: relative;
`;

const ActionMenu = styled.div`
  position: absolute;
  top: calc(100% + ${theme.spacing[1]});
  right: 0;
  min-width: 160px;
  background: ${theme.colors.surface};
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.borderRadius.md};
  box-shadow: ${theme.shadows.lg};
  padding: ${theme.spacing[1]};
  z-index: 10;
`;

const ActionMenuItem = styled.button<{ $danger?: boolean }>`
  display: flex;
  width: 100%;
  align-items: center;
  gap: ${theme.spacing[2]};
  padding: ${theme.spacing[2]} ${theme.spacing[3]};
  background: transparent;
  border: none;
  border-radius: ${theme.borderRadius.sm};
  color: ${({ $danger }) => ($danger ? theme.colors.error[600] : theme.colors.textPrimary)};
  font-size: ${theme.typography.fontSize.sm};
  cursor: pointer;
  text-align: left;

  &:hover {
    background: ${({ $danger }) => ($danger ? theme.colors.error[100] : theme.colors.backgroundSecondary)};
  }
`;

const MetadataRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing[4]};
  margin-bottom: ${theme.spacing[3]};
  color: ${theme.colors.textMuted};
  font-size: ${theme.typography.fontSize.sm};
  flex-wrap: wrap;
`;

const MetadataItem = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing[1]};
`;

const MetadataSeparator = styled.span`
  color: ${theme.colors.border};
`;

const SummaryText = styled.p`
  color: ${theme.colors.textSecondary};
  font-size: ${theme.typography.fontSize.sm};
  line-height: ${theme.typography.lineHeight.normal};
  margin: 0;
  
  /* Truncate to 1 line */
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const StatsRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing[6]};
  margin-top: ${theme.spacing[4]};
  padding-top: ${theme.spacing[3]};
  border-top: 1px solid ${theme.colors.borderLight};
`;

const StatItem = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing[2]};
  color: ${theme.colors.textMuted};
  font-size: ${theme.typography.fontSize.xs};
`;

const StatValue = styled.span`
  font-weight: ${theme.typography.fontWeight.semibold};
  color: ${theme.colors.textPrimary};
`;

interface TenderDocument {
  id: string;
  title: string;
  company: string;
  industry: string;
  date: string;
  // score is optional — many uploads come in without a known evaluator
  // score (the user just dropped a tender doc to mine for patterns).
  // Rendering 0% in that case is misleading; we show "Not scored"
  // instead. When score IS provided, the badge tones (success/warn/etc)
  // colour based on its value.
  score?: number | null;
  usageCount: number;
  summary: string;
  patterns?: number;
}

interface TenderRowProps {
  document: TenderDocument;
  onClick: () => void;
  onDelete?: (id: string) => void;
}

// Render-friendly display title. When extraction fails the backend
// falls back to using the source URL as the title — that produces
// "Tender Document from https://res.cloudinary.com/.../v1755171537/..."
// strings that bust the layout. Strip the URL down to its filename
// stem and add a friendly prefix so the row stays readable while
// keeping the document identifiable.
const _isUrlLike = (s: string) =>
  /^https?:\/\//i.test(s) || /from\s+https?:\/\//i.test(s);

const _displayTitle = (raw: string): string => {
  if (!raw) return 'Untitled Document';
  if (!_isUrlLike(raw)) return raw;
  // Pull the URL out — supports both "https://..." and "Tender Document from https://..."
  const match = raw.match(/https?:\/\/\S+/i);
  if (!match) return raw;
  try {
    const url = new URL(match[0]);
    const lastSeg = url.pathname.split('/').filter(Boolean).pop() || '';
    // Strip extension + Cloudinary's vNNNN cache-buster prefix on the path
    const cleaned = lastSeg.replace(/\.[a-z0-9]{2,5}$/i, '').slice(0, 60);
    if (cleaned) return `Tender — ${cleaned}`;
  } catch {
    /* fall through */
  }
  return 'Tender Document (failed to fetch)';
};

const TenderRow: React.FC<TenderRowProps> = ({ document, onClick, onDelete }) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const anchorRef = useRef<HTMLDivElement | null>(null);

  // Close on outside click / Escape — same pattern any popover needs.
  // Without this the menu would stay sticky after clicking elsewhere
  // and feel half-broken.
  useEffect(() => {
    if (!menuOpen) return;
    const onDocClick = (e: MouseEvent) => {
      if (!anchorRef.current) return;
      if (!anchorRef.current.contains(e.target as Node)) setMenuOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setMenuOpen(false);
    };
    window.document.addEventListener('mousedown', onDocClick);
    window.document.addEventListener('keydown', onKey);
    return () => {
      window.document.removeEventListener('mousedown', onDocClick);
      window.document.removeEventListener('keydown', onKey);
    };
  }, [menuOpen]);

  const handleActionClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setMenuOpen(prev => !prev);
  };

  const handleDeleteClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setMenuOpen(false);
    if (!onDelete) return;
    onDelete(document.id);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const getIndustryIcon = (industry: string) => {
    switch (industry.toLowerCase()) {
      case 'infrastructure':
        return '🏗️';
      case 'technology':
        return '💻';
      case 'healthcare':
        return '🏥';
      case 'energy':
        return '⚡';
      case 'education':
        return '📚';
      default:
        return '🏢';
    }
  };

  const getScoreIcon = (score: number | null | undefined) => {
    if (score == null) return <Target size={16} />;
    if (score >= 90) return <Award size={16} />;
    if (score >= 70) return <TrendingUp size={16} />;
    if (score >= 50) return <Target size={16} />;
    return <Target size={16} />;
  };

  return (
    <RowContainer onClick={onClick}>
      <RowHeader>
        <TitleSection>
          <DocumentIcon>
            <FileText size={20} />
          </DocumentIcon>
          <TitleContent>
            <DocumentTitle>{_displayTitle(document.title)}</DocumentTitle>
          </TitleContent>
        </TitleSection>
        
        <ScoreSection>
          <ScoreBadge $score={document.score ?? null}>
            {getScoreIcon(document.score)}
            {document.score == null ? 'Not scored' : `${document.score}%`}
          </ScoreBadge>
          <IndustryIcon $industry={document.industry}>
            {getIndustryIcon(document.industry)}
          </IndustryIcon>
          <ActionAnchor ref={anchorRef}>
            <ActionButton onClick={handleActionClick} aria-haspopup="menu" aria-expanded={menuOpen}>
              <MoreHorizontal size={16} />
            </ActionButton>
            {menuOpen && (
              <ActionMenu role="menu" onClick={(e) => e.stopPropagation()}>
                <ActionMenuItem
                  $danger
                  role="menuitem"
                  onClick={handleDeleteClick}
                  disabled={!onDelete}
                >
                  <Trash2 size={14} />
                  Delete
                </ActionMenuItem>
              </ActionMenu>
            )}
          </ActionAnchor>
        </ScoreSection>
      </RowHeader>

      <MetadataRow>
        <MetadataItem>
          <Building2 size={14} />
          {document.company}
        </MetadataItem>
        <MetadataSeparator>•</MetadataSeparator>
        <MetadataItem>
          {document.industry}
        </MetadataItem>
        <MetadataSeparator>•</MetadataSeparator>
        <MetadataItem>
          <Calendar size={14} />
          {formatDate(document.date)}
        </MetadataItem>
        <MetadataSeparator>•</MetadataSeparator>
        <MetadataItem>
          <Users size={14} />
          Used in {document.usageCount} project{document.usageCount !== 1 ? 's' : ''}
        </MetadataItem>
      </MetadataRow>

      <SummaryText>{document.summary}</SummaryText>

      <StatsRow>
        <StatItem>
          <Target size={14} />
          <StatValue>{document.patterns || 0}</StatValue> patterns found
        </StatItem>
        <StatItem>
          <TrendingUp size={14} />
          <StatValue>{document.score == null ? '—' : `${document.score}%`}</StatValue> success score
        </StatItem>
        <StatItem>
          <Users size={14} />
          <StatValue>{document.usageCount}x</StatValue> referenced
        </StatItem>
        <StatItem>
          <Calendar size={14} />
          Added {formatDate(document.date)}
        </StatItem>
      </StatsRow>
    </RowContainer>
  );
};

export default TenderRow;